import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

// Composants
import { BaseComponent } from '../abstract/base.component';
import { MessageAction, MessageComponent } from '../message/message.component';

// Services
import { AGENT_MARKER_NOT_FOUND } from 'src/app/@core/services/ai/agent-marker.service';

// Modèles
import { AgentMarker } from '../../models/ai/agent-marker.model';

// Utils
import { ODataError } from '../../utilities/odata/response';

/**
 * Mention « écrit par l'assistant » d'un objet métier (#1217) : une étincelle seule, dont le
 * popover dit à la demande de qui et quand, avec Conserver et Annuler seulement si le serveur
 * les accepterait pour l'utilisateur courant. Une page la reçoit dans le shell, à côté de la
 * documentation (`AppService.agentMarkerTarget`) ; une fenêtre la pose en bas à gauche, face
 * à ses boutons d'enregistrement.
 *
 * Le composant joue le geste ; l'hôte, qui connaît l'objet, en tire la suite : `undone`
 * quand l'objet n'existe plus (une création annulée est supprimée — l'hôte quitte la page
 * ou ferme la fenêtre), `cleared` quand l'objet reste mais que le marqueur n'est plus —
 * conservé, déjà revu par quelqu'un d'autre, ou modification annulée par la transition
 * inverse (l'hôte relit l'objet). Un refus métier ou un droit manquant s'affiche et ne
 * change rien. Un geste réussi referme le popover.
 */
@Component({
    selector: 'app-agent-marker',
    imports: [
        ButtonModule,
        MessageComponent,
        PopoverModule,
        TooltipModule,
    ],
    templateUrl: './agent-marker.component.html',
})
export class AgentMarkerComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @Output() cleared = new EventEmitter<void>();
    @Output() undone = new EventEmitter<void>();

    public primaryAction?: MessageAction;
    public secondaryActions: MessageAction[] = [];
    public title: string = '';
    /** À droite du titre : l'utilisateur au nom duquel l'assistant a agi, et quand. */
    public titleNote: string = '';

    @ViewChild('popover') private _popover?: Popover;

    private _pending: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _marker!: AgentMarker;
    public get marker(): AgentMarker {
        return this._marker;
    }
    @Input() public set marker(marker: AgentMarker) {
        this._marker = marker;
        this._buildTitle();
        this._buildActions();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickKeep(): void {
        this._keep();
    }

    public onClickToggle(event: Event): void {
        this._popover?.toggle(event);
    }

    public onClickUndo(): void {
        this._confirmUndo();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /** Boutons mémoïsés : reconstruits quand le marqueur ou l'état du geste changent. */
    private _buildActions(): void {
        this.primaryAction = this._marker.canKeep
            ? { label: 'Conserver', handler: () => this.onClickKeep(), loading: this._pending }
            : undefined;
        this.secondaryActions = this._marker.canUndo
            ? [{ label: 'Annuler', handler: () => this.onClickUndo(), loading: this._pending }]
            : [];
    }

    /**
     * Le titre dit ce qu'a fait l'assistant quand le marqueur le permet : une création. Une
     * modification ne dit pas laquelle (validation, dévalidation…), d'où le titre générique.
     */
    private _buildTitle(): void {
        const date = this._marker.creationDate?.format('DD/MM/YYYY [à] HH:mm');
        this.title = this._marker.nature === 'creation' ? 'Créé par l\'assistant' : 'Écrit par l\'assistant';
        this.titleNote = 'Initié par ' + [this._marker.user?.fullName, date].filter(Boolean).join(' le ');
    }

    private _confirmUndo(): void {
        this.confirmAction('UNDO_AGENT_MARKER', () => this._undo());
    }

    private _keep(): void {
        this._setPending(true);
        this.subscriptions.add(
            this.agentMarkerService.keep$(this._marker.id).pipe(
                finalize(() => this._setPending(false))
            ).subscribe({
                next: () => this._onKept(),
                error: (error: ODataError) => this._onGestureError(error)
            })
        );
    }

    /** « Déjà revu » : le marqueur n'est plus, l'hôte relit l'objet. Tout autre refus s'est affiché. */
    private _onGestureError(error: ODataError): void {
        if (error?.messages?.some(message => message.code === AGENT_MARKER_NOT_FOUND)) {
            this._popover?.hide();
            this.cleared.emit();
        }
    }

    private _onKept(): void {
        this._popover?.hide();
        this.cleared.emit();
    }

    /** Une création annulée n'existe plus ; une modification annulée laisse l'objet en place. */
    private _onUndone(): void {
        this._popover?.hide();
        if (this._marker.nature === 'creation') {
            this.undone.emit();
        } else {
            this.cleared.emit();
        }
    }

    private _setPending(pending: boolean): void {
        this._pending = pending;
        this._buildActions();
    }

    private _undo(): void {
        this._setPending(true);
        this.subscriptions.add(
            this.agentMarkerService.undo$(this._marker.id).pipe(
                finalize(() => this._setPending(false))
            ).subscribe({
                next: () => this._onUndone(),
                error: (error: ODataError) => this._onGestureError(error)
            })
        );
    }
}
